import { Fragment, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import {
  ADMIN_CONVERSION_ALERTS_QUERY,
  ADMIN_CONVERSION_STATS_QUERY,
  ADMIN_CONVERSION_METRICS_QUERY,
} from '../../graphql/queries';
import styles from './AdminConversionStats.module.css';

interface ConversionAlert {
  severity: 'critical' | 'warning' | 'info';
  code: string;
  message: string;
  lane: string | null;
  metricId: string | null;
  since: string | null;
}

interface LaneStats {
  lane: string;
  capacity: number;
  runningNow: number;
  queuedNow: number;
  longestCurrentWaitSeconds: number | null;
  jobs: number;
  failed: number;
  avgQueueSeconds: number | null;
  p50QueueSeconds: number | null;
  p95QueueSeconds: number | null;
  maxQueueSeconds: number | null;
  p50CrossUserWaitSeconds: number | null;
  p95CrossUserWaitSeconds: number | null;
  maxCrossUserWaitSeconds: number | null;
  avgRunSeconds: number | null;
  p50RunSeconds: number | null;
  p95RunSeconds: number | null;
  maxRunSeconds: number | null;
}

interface ModeStats {
  mode: string;
  jobs: number;
  failed: number;
  p50QueueSeconds: number | null;
  p95QueueSeconds: number | null;
  p50RunSeconds: number | null;
  p95RunSeconds: number | null;
  avgRunSeconds: number | null;
}

interface DailyStat {
  date: string;
  jobs: number;
  failed: number;
  heavy: number;
  standard: number;
  light: number;
  gpu: number;
  p50QueueSeconds: number | null;
  p95QueueSeconds: number | null;
  p50RunSeconds: number | null;
  p95RunSeconds: number | null;
}

interface MetricUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface SlowWait {
  id: string;
  jobName: string;
  fileName: string | null;
  lane: string;
  mode: string;
  queueSeconds: number | null;
  crossUserWaitSeconds: number | null;
  submittedAt: string;
  user: MetricUser | null;
}

interface TopWaitingUser {
  user: MetricUser | null;
  jobs: number;
  totalCrossUserWaitSeconds: number;
  maxCrossUserWaitSeconds: number;
}

interface StatsReport {
  rangeDays: number;
  totalJobs: number;
  succeeded: number;
  failed: number;
  runningNow: number;
  queuedNow: number;
  successRate: number | null;
  p50QueueSeconds: number | null;
  p95QueueSeconds: number | null;
  maxQueueSeconds: number | null;
  p50CrossUserWaitSeconds: number | null;
  p95CrossUserWaitSeconds: number | null;
  maxCrossUserWaitSeconds: number | null;
  p50RunSeconds: number | null;
  p95RunSeconds: number | null;
  lanes: LaneStats[];
  modes: ModeStats[];
  daily: DailyStat[];
  slowestWaits: SlowWait[];
  topWaitingUsers: TopWaitingUser[];
}

interface Metric {
  id: string;
  jobId: string | null;
  jobName: string;
  mode: string;
  lane: string;
  status: string;
  batchStatus: string | null;
  statusReason: string | null;
  exitCode: number | null;
  errorMessage: string | null;
  fileId: string | null;
  fileName: string | null;
  fileExt: string | null;
  fileSizeBytes: number | null;
  user: MetricUser | null;
  project: { id: string; name: string } | null;
  schedulingPriority: number | null;
  submittedAt: string;
  startedAt: string | null;
  stoppedAt: string | null;
  queueSeconds: number | null;
  runSeconds: number | null;
  crossUserWaitSeconds: number | null;
}

const RANGE_TABS = [
  { value: 1, label: '24 h' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
];

const LANE_TABS = [
  { value: '', label: 'All lanes' },
  { value: 'heavy', label: 'Heavy' },
  { value: 'standard', label: 'Standard' },
  { value: 'light', label: 'Light' },
  { value: 'gpu', label: 'GPU' },
];

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
];

const MODE_OPTIONS = ['', 'convert', 'dwg2svg', 'ifc2glb', 'harmonize', 'rebuild', 'gaussian', 'ortho2cog', 'video360'];

const LANE_COLORS: Record<string, string> = {
  heavy: '#6366f1',
  standard: '#14b8a6',
  light: '#f59e0b',
  gpu: '#d946ef',
  legacy: '#9ca3af',
};

const STATUS_CONFIG: Record<string, { label: string; badgeClass: string; dotClass: string; pulse?: boolean }> = {
  submitted:     { label: 'Queued',        badgeClass: styles.statusPending, dotClass: 'bg-yellow-400', pulse: true },
  pending:       { label: 'Queued',        badgeClass: styles.statusPending, dotClass: 'bg-yellow-400', pulse: true },
  running:       { label: 'Running',       badgeClass: styles.statusRunning, dotClass: 'bg-blue-500', pulse: true },
  succeeded:     { label: 'Succeeded',     badgeClass: styles.statusSuccess, dotClass: 'bg-green-500' },
  failed:        { label: 'Failed',        badgeClass: styles.statusFailed,  dotClass: 'bg-red-500' },
  submit_failed: { label: 'Submit failed', badgeClass: styles.statusFailed,  dotClass: 'bg-red-500' },
  lost:          { label: 'Lost',          badgeClass: styles.statusLost,    dotClass: 'bg-gray-400' },
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—';
  if (seconds < 1) return '<1 s';
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} m ${s % 60} s`;
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} m`;
};

const formatSize = (bytes: number | null | undefined): string => {
  if (bytes == null) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (s: string | null | undefined) => s
  ? new Date(s).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  : '—';

const userLabel = (u: MetricUser | null) =>
  u ? (`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email) : '—';

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.lost;
  return (
    <span className={`${styles.statusBadge} ${cfg.badgeClass}`}>
      <span className={`${styles.dot} ${cfg.dotClass} ${cfg.pulse ? styles.dotPulse : ''}`} />
      {cfg.label}
    </span>
  );
}

function LaneBadge({ lane }: { lane: string }) {
  return (
    <span className={styles.laneBadge} style={{ background: `${LANE_COLORS[lane] || '#9ca3af'}18`, color: LANE_COLORS[lane] || '#6b7280' }}>
      <span className={styles.laneDot} style={{ background: LANE_COLORS[lane] || '#9ca3af' }} />
      {lane}
    </span>
  );
}

// Hand-rolled SVG charts — the frontend has no charting dependency, and two
// small charts don't justify adding one.

function JobsPerDayChart({ daily }: { daily: DailyStat[] }) {
  const W = 640, H = 200, PAD_L = 34, PAD_B = 22, PAD_T = 10;
  const maxJobs = Math.max(1, ...daily.map((d) => d.jobs));
  const innerW = W - PAD_L - 8;
  const innerH = H - PAD_T - PAD_B;
  const barW = Math.min(34, (innerW / Math.max(1, daily.length)) * 0.7);
  const step = innerW / Math.max(1, daily.length);
  const yTicks = [...new Set([0, Math.ceil(maxJobs / 2), maxJobs])];
  const labelEvery = Math.max(1, Math.ceil(daily.length / 8));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={styles.chartSvg} role="img" aria-label="Jobs per day by lane">
      {yTicks.map((t) => {
        const y = PAD_T + innerH - (t / maxJobs) * innerH;
        return (
          <g key={t}>
            <line x1={PAD_L} y1={y} x2={W - 8} y2={y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#9ca3af">{t}</text>
          </g>
        );
      })}
      {daily.map((d, i) => {
        const x = PAD_L + i * step + (step - barW) / 2;
        let y = PAD_T + innerH;
        const segs = [
          { v: d.heavy, c: LANE_COLORS.heavy },
          { v: d.standard, c: LANE_COLORS.standard },
          { v: d.light, c: LANE_COLORS.light },
          { v: d.gpu, c: LANE_COLORS.gpu },
          { v: d.jobs - d.heavy - d.standard - d.light - d.gpu, c: LANE_COLORS.legacy },
        ];
        return (
          <g key={d.date}>
            <title>{`${d.date}: ${d.jobs} jobs (${d.failed} failed)`}</title>
            {segs.map((s, j) => {
              if (s.v <= 0) return null;
              const h = (s.v / maxJobs) * innerH;
              y -= h;
              return <rect key={j} x={x} y={y} width={barW} height={h} fill={s.c} rx="1.5" />;
            })}
            {d.failed > 0 && (
              <circle cx={x + barW / 2} cy={PAD_T + innerH - (d.jobs / maxJobs) * innerH - 6} r="3" fill="#ef4444" />
            )}
            {i % labelEvery === 0 && (
              <text x={x + barW / 2} y={H - 6} textAnchor="middle" fontSize="9" fill="#9ca3af">
                {d.date.slice(5)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function WaitTrendChart({ daily }: { daily: DailyStat[] }) {
  const W = 640, H = 200, PAD_L = 44, PAD_B = 22, PAD_T = 10;
  const innerW = W - PAD_L - 8;
  const innerH = H - PAD_T - PAD_B;
  const maxWait = Math.max(60, ...daily.map((d) => d.p95QueueSeconds ?? 0));
  const x = (i: number) => PAD_L + (daily.length <= 1 ? innerW / 2 : (i / (daily.length - 1)) * innerW);
  const y = (v: number) => PAD_T + innerH - (v / maxWait) * innerH;
  const line = (pick: (d: DailyStat) => number | null) =>
    daily.map((d, i) => ({ d, i, v: pick(d) })).filter((p) => p.v != null)
      .map((p) => `${x(p.i).toFixed(1)},${y(p.v as number).toFixed(1)}`).join(' ');
  const labelEvery = Math.max(1, Math.ceil(daily.length / 8));
  const yTicks = [0, maxWait / 2, maxWait];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={styles.chartSvg} role="img" aria-label="Queue wait per day">
      {yTicks.map((t) => (
        <g key={t}>
          <line x1={PAD_L} y1={y(t)} x2={W - 8} y2={y(t)} stroke="#e5e7eb" strokeWidth="1" />
          <text x={PAD_L - 6} y={y(t) + 3} textAnchor="end" fontSize="9" fill="#9ca3af">
            {t >= 60 ? `${Math.round(t / 60)}m` : `${Math.round(t)}s`}
          </text>
        </g>
      ))}
      <polyline points={line((d) => d.p95QueueSeconds)} fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinejoin="round" />
      <polyline points={line((d) => d.p50QueueSeconds)} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round" />
      {daily.map((d, i) => (
        <g key={d.date}>
          {d.p95QueueSeconds != null && <circle cx={x(i)} cy={y(d.p95QueueSeconds)} r="2.5" fill="#f59e0b" />}
          {d.p50QueueSeconds != null && <circle cx={x(i)} cy={y(d.p50QueueSeconds)} r="2.5" fill="#6366f1" />}
          {i % labelEvery === 0 && (
            <text x={x(i)} y={H - 6} textAnchor="middle" fontSize="9" fill="#9ca3af">{d.date.slice(5)}</text>
          )}
        </g>
      ))}
    </svg>
  );
}

const ALERT_STYLES: Record<string, string> = {
  critical: styles.alertCritical,
  warning: styles.alertWarning,
  info: styles.alertInfo,
};

const ALERT_ICONS: Record<string, string> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

const AdminConversionStats = () => {
  const navigate = useNavigate();
  const [rangeDays, setRangeDays] = useState(7);
  const [laneFilter, setLaneFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // The live "waiting…"/"running…" cells need a clock: Apollo does not re-render
  // when a poll returns data identical to the cache, so Date.now() at render
  // would freeze.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (searchInput === search) return undefined;
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput, search]);

  const { data: alertsData, error: alertsError } = useQuery(ADMIN_CONVERSION_ALERTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30_000,
  });

  const { data: statsData, loading: statsLoading, error: statsError } = useQuery(ADMIN_CONVERSION_STATS_QUERY, {
    variables: { rangeDays },
    fetchPolicy: 'cache-and-network',
    pollInterval: 60_000,
  });

  const { data: metricsData, previousData: previousMetricsData, loading: metricsLoading, error: metricsError } = useQuery(ADMIN_CONVERSION_METRICS_QUERY, {
    variables: {
      page,
      limit: 25,
      lane: laneFilter || undefined,
      mode: modeFilter || undefined,
      status: statusFilter || undefined,
      search: search || undefined,
      sinceDays: rangeDays,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30_000,
  });

  const alerts: ConversionAlert[] = alertsData?.adminConversionAlerts ?? [];
  const stats: StatsReport | undefined = statsData?.adminConversionStats;
  const metrics: Metric[] = metricsData?.adminConversionMetrics?.metrics ?? [];
  // While another page loads `data` is empty; the last known total keeps the
  // clamp below from snapping the admin back to page 1.
  const total: number = (metricsData ?? previousMetricsData)?.adminConversionMetrics?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));
  const loadError = alertsError || statsError || metricsError;

  // Jobs age out of the sliding range window under polling — clamp so the admin
  // is never stranded on an empty page with no pagination controls.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const totalCapacity = stats?.lanes
    .filter((l) => l.lane !== 'legacy')
    .reduce((s, l) => s + l.capacity, 0) ?? 0;

  const statCards = [
    { label: 'Running now', value: stats ? `${stats.runningNow} / ${totalCapacity}` : '—', icon: 'play_circle', color: 'bg-blue-50 text-blue-600' },
    { label: 'Queued now', value: stats?.queuedNow ?? '—', icon: 'hourglass_top', color: 'bg-amber-50 text-amber-600' },
    { label: `Jobs (${rangeDays} d)`, value: stats?.totalJobs ?? '—', icon: 'transform', color: 'bg-indigo-50 text-indigo-600' },
    { label: 'Success rate', value: stats?.successRate != null ? `${Math.round(stats.successRate * 100)} %` : '—', icon: 'verified', color: 'bg-green-50 text-green-600' },
    { label: 'Contended wait p50', value: formatDuration(stats?.p50CrossUserWaitSeconds), icon: 'schedule', color: 'bg-teal-50 text-teal-600' },
    { label: 'Contended wait max', value: formatDuration(stats?.maxCrossUserWaitSeconds), icon: 'timer', color: 'bg-orange-50 text-orange-600' },
    { label: 'Median runtime', value: formatDuration(stats?.p50RunSeconds), icon: 'speed', color: 'bg-purple-50 text-purple-600' },
    { label: 'P95 runtime', value: formatDuration(stats?.p95RunSeconds), icon: 'av_timer', color: 'bg-rose-50 text-rose-600' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-end flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-1 text-sm text-on-surface-variant mb-1">
              <button onClick={() => navigate('/admin')} className="hover:text-primary transition-colors">Admin</button>
              <span className="material-symbols-outlined text-sm">chevron_right</span>
              <span>Conversion Monitor</span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Conversion Monitor</h2>
            <p className="text-on-surface-variant mt-1">
              Queue waits, runtimes and lane capacity for AWS Batch conversion jobs — use this to decide when to scale.
            </p>
            <p className="text-on-surface-variant text-xs mt-1">
              <span className="font-semibold">Contended wait</span> counts only time a job waited while <span className="font-semibold">other users'</span> jobs occupied its lane — a user queueing behind their own batch upload scores zero and raises no alerts.
            </p>
          </div>
          <div className="flex items-center gap-1 bg-surface-container rounded-xl p-1.5 w-fit">
            {RANGE_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => { setRangeDays(tab.value); setPage(1); }}
                className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  rangeDays === tab.value
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Load errors — a monitoring page must never report "all clear" while blind */}
        {loadError && (
          <div className={`${styles.alertRow} ${styles.alertCritical}`}>
            <span className="material-symbols-outlined text-lg">cloud_off</span>
            <div className="flex-1 min-w-0">
              <p className={styles.alertMessage}>Failed to load conversion monitoring data: {loadError.message}</p>
            </div>
          </div>
        )}

        {/* Alerts */}
        {alertsError || !alertsData ? null : alerts.length === 0 ? (
          <div className={styles.alertAllClear}>
            <span className="material-symbols-outlined text-lg">check_circle</span>
            All clear — no queue-wait, capacity or failure alerts.
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.map((a, i) => (
              <div key={`${a.code}-${a.metricId ?? i}`} className={`${styles.alertRow} ${ALERT_STYLES[a.severity] || styles.alertInfo}`}>
                <span className="material-symbols-outlined text-lg">{ALERT_ICONS[a.severity] || 'info'}</span>
                <div className="flex-1 min-w-0">
                  <p className={styles.alertMessage}>{a.message}</p>
                  {a.since && <p className={styles.alertSince}>since {formatDate(a.since)}</p>}
                </div>
                <span className={styles.alertCode}>{a.code}</span>
              </div>
            ))}
          </div>
        )}

        {/* Stat cards */}
        {statsLoading && !stats ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
            Loading statistics...
          </div>
        ) : (
          <div className={styles.statsGrid}>
            {statCards.map((card) => (
              <div key={card.label} className="bg-white rounded-xl p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-on-surface-variant mb-1">{card.label}</p>
                    <p className="text-2xl font-bold text-on-surface font-headline whitespace-nowrap">{card.value}</p>
                  </div>
                  <div className={`p-2.5 rounded-xl ${card.color}`}>
                    <span className="material-symbols-outlined text-xl">{card.icon}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lane capacity table */}
        {stats && (
          <div className="bg-white rounded-xl overflow-hidden">
            <div className="px-6 pt-5 pb-2">
              <h3 className="font-bold text-on-surface">Lanes</h3>
              <p className="text-xs text-on-surface-variant mt-0.5">
                Live occupancy against each lane's parallel-job capacity. Sustained queueing on a lane means its compute environment needs more maxVCPUs.
              </p>
            </div>
            <div className={styles.tableWrap}>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-surface-container-low">
                  <th className="table-header">Lane</th>
                  <th className="table-header">Occupancy</th>
                  <th className="table-header">Queued</th>
                  <th className="table-header">Jobs</th>
                  <th className="table-header">Failed</th>
                  <th className="table-header">Contended wait p50 / max</th>
                  <th className="table-header">Total wait p50 / p95 / max</th>
                  <th className="table-header">Runtime p50 / p95 / max</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10">
                {stats.lanes.map((l) => {
                  const saturated = l.capacity > 0 && l.runningNow >= l.capacity && l.queuedNow > 0;
                  return (
                    <tr key={l.lane} className="table-row">
                      <td className="px-6 py-4"><LaneBadge lane={l.lane} /></td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className={styles.occupancyTrack}>
                            <div
                              className={styles.occupancyFill}
                              style={{
                                width: `${l.capacity > 0 ? Math.min(100, (l.runningNow / l.capacity) * 100) : 0}%`,
                                background: saturated ? '#ef4444' : LANE_COLORS[l.lane] || '#9ca3af',
                              }}
                            />
                          </div>
                          <span className="text-xs text-on-surface-variant whitespace-nowrap">
                            {l.runningNow} / {l.capacity || '—'}
                          </span>
                          {saturated && <span className={styles.saturatedBadge}>saturated</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-on-surface-variant">
                        {l.queuedNow}
                        {l.longestCurrentWaitSeconds != null && l.queuedNow > 0 && (
                          <span className="text-amber-600"> · longest {formatDuration(l.longestCurrentWaitSeconds)}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs text-on-surface-variant">{l.jobs}</td>
                      <td className={`px-6 py-4 text-xs ${l.failed > 0 ? 'text-error font-semibold' : 'text-on-surface-variant'}`}>{l.failed}</td>
                      <td className={`px-6 py-4 text-xs whitespace-nowrap ${(l.maxCrossUserWaitSeconds ?? 0) > 0 ? 'text-amber-700 font-semibold' : 'text-on-surface-variant'}`}>
                        {formatDuration(l.p50CrossUserWaitSeconds)} / {formatDuration(l.maxCrossUserWaitSeconds)}
                      </td>
                      <td className="px-6 py-4 text-xs text-on-surface-variant whitespace-nowrap">
                        {formatDuration(l.p50QueueSeconds)} / {formatDuration(l.p95QueueSeconds)} / {formatDuration(l.maxQueueSeconds)}
                      </td>
                      <td className="px-6 py-4 text-xs text-on-surface-variant whitespace-nowrap">
                        {formatDuration(l.p50RunSeconds)} / {formatDuration(l.p95RunSeconds)} / {formatDuration(l.maxRunSeconds)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Charts */}
        {stats && stats.daily.length > 0 && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-on-surface">Jobs per day</h3>
                <div className={styles.legend}>
                  <span><i style={{ background: LANE_COLORS.heavy }} /> heavy</span>
                  <span><i style={{ background: LANE_COLORS.standard }} /> standard</span>
                  <span><i style={{ background: LANE_COLORS.light }} /> light</span>
                  <span><i style={{ background: LANE_COLORS.gpu }} /> gpu</span>
                  <span><i style={{ background: '#ef4444', borderRadius: '50%' }} /> has failures</span>
                </div>
              </div>
              <JobsPerDayChart daily={stats.daily} />
            </div>
            <div className="bg-white rounded-xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-on-surface">Queue wait per day</h3>
                <div className={styles.legend}>
                  <span><i style={{ background: '#6366f1' }} /> median</span>
                  <span><i style={{ background: '#f59e0b' }} /> p95</span>
                </div>
              </div>
              <WaitTrendChart daily={stats.daily} />
            </div>
          </div>
        )}

        {/* By type + slowest waits */}
        {stats && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl overflow-hidden">
              <div className="px-6 pt-5 pb-2">
                <h3 className="font-bold text-on-surface">By job type</h3>
              </div>
              <div className={styles.tableWrap}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="table-header">Type</th>
                    <th className="table-header">Jobs</th>
                    <th className="table-header">Failed</th>
                    <th className="table-header">Wait p50 / p95</th>
                    <th className="table-header">Runtime p50 / p95</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {stats.modes.length === 0 && (
                    <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-on-surface-variant">No jobs in this range</td></tr>
                  )}
                  {stats.modes.map((m) => (
                    <tr key={m.mode} className="table-row">
                      <td className="px-6 py-3 text-xs font-semibold text-on-surface">{m.mode}</td>
                      <td className="px-6 py-3 text-xs text-on-surface-variant">{m.jobs}</td>
                      <td className={`px-6 py-3 text-xs ${m.failed > 0 ? 'text-error font-semibold' : 'text-on-surface-variant'}`}>{m.failed}</td>
                      <td className="px-6 py-3 text-xs text-on-surface-variant whitespace-nowrap">
                        {formatDuration(m.p50QueueSeconds)} / {formatDuration(m.p95QueueSeconds)}
                      </td>
                      <td className="px-6 py-3 text-xs text-on-surface-variant whitespace-nowrap">
                        {formatDuration(m.p50RunSeconds)} / {formatDuration(m.p95RunSeconds)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden">
              <div className="px-6 pt-5 pb-2">
                <h3 className="font-bold text-on-surface">Longest contended waits</h3>
                <p className="text-xs text-on-surface-variant mt-0.5">Jobs that waited because OTHER users' jobs held the lane — the clearest scale-up signal. Own-batch queueing is excluded.</p>
              </div>
              {stats.slowestWaits.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-on-surface-variant">
                  No contended waits — all queueing in this range was users waiting behind their own uploads.
                </div>
              ) : (
                <ul className="divide-y divide-outline-variant/10">
                  {stats.slowestWaits.map((w) => (
                    <li key={w.id} className="px-6 py-3 flex items-center gap-3">
                      <span className={styles.waitTime}>{formatDuration(w.crossUserWaitSeconds)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-on-surface truncate">{w.fileName || w.jobName}</p>
                        <p className="text-[11px] text-on-surface-variant truncate">
                          {userLabel(w.user)} · {formatDate(w.submittedAt)} · {formatDuration(w.queueSeconds)} total wait
                        </p>
                      </div>
                      <LaneBadge lane={w.lane} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white rounded-xl overflow-hidden">
              <div className="px-6 pt-5 pb-2">
                <h3 className="font-bold text-on-surface">Waits by user</h3>
                <p className="text-xs text-on-surface-variant mt-0.5">Total contended wait per user in the range — who is actually feeling the queue.</p>
              </div>
              {stats.topWaitingUsers.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-on-surface-variant">
                  No user was delayed by other users' jobs in this range.
                </div>
              ) : (
                <div className={styles.tableWrap}>
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-surface-container-low">
                      <th className="table-header">User</th>
                      <th className="table-header">Delayed jobs</th>
                      <th className="table-header">Total contended wait</th>
                      <th className="table-header">Worst single wait</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/10">
                    {stats.topWaitingUsers.map((u, i) => (
                      <tr key={u.user?.id ?? i} className="table-row">
                        <td className="px-6 py-3">
                          <p className="text-sm text-on-surface truncate max-w-[180px]">{userLabel(u.user)}</p>
                          {u.user?.email && <p className="text-[10px] text-on-surface-variant truncate max-w-[180px]">{u.user.email}</p>}
                        </td>
                        <td className="px-6 py-3 text-xs text-on-surface-variant">{u.jobs}</td>
                        <td className="px-6 py-3 text-xs font-semibold text-amber-700 whitespace-nowrap">{formatDuration(u.totalCrossUserWaitSeconds)}</td>
                        <td className="px-6 py-3 text-xs text-on-surface-variant whitespace-nowrap">{formatDuration(u.maxCrossUserWaitSeconds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Jobs detail table */}
        <div className="bg-white rounded-xl overflow-hidden">
          <div className="px-6 pt-5 pb-3 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-bold text-on-surface">All jobs</h3>
              <p className="text-xs text-on-surface-variant mt-0.5">{total} job records in the selected range</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                className="input-field !w-52 max-w-full !py-1.5 text-sm"
                placeholder="Search file or job name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <select
                className="input-field !w-auto !py-1.5 text-sm"
                value={laneFilter}
                onChange={(e) => { setLaneFilter(e.target.value); setPage(1); }}
              >
                {LANE_TABS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select
                className="input-field !w-auto !py-1.5 text-sm"
                value={modeFilter}
                onChange={(e) => { setModeFilter(e.target.value); setPage(1); }}
              >
                {MODE_OPTIONS.map((m) => <option key={m} value={m}>{m || 'All types'}</option>)}
              </select>
              <div className="flex items-center gap-1 bg-surface-container-low rounded-xl p-1">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => { setStatusFilter(tab.value); setPage(1); }}
                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-all ${
                      statusFilter === tab.value
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {metricsLoading && metrics.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">
              <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
              Loading jobs...
            </div>
          ) : metrics.length === 0 ? (
            <div className="py-16 text-center">
              <span className="material-symbols-outlined text-4xl text-outline mb-3 block">{metricsError ? 'cloud_off' : 'monitoring'}</span>
              <p className="text-on-surface-variant">{metricsError ? 'Failed to load job records' : 'No job records found'}</p>
              <p className="text-xs text-on-surface-variant mt-1">
                {metricsError ? metricsError.message : 'Records are collected from the moment this feature is deployed — trigger a conversion to see the first one.'}
              </p>
            </div>
          ) : (
            <>
              <div className={styles.tableWrap}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="table-header">Job</th>
                    <th className="table-header">User</th>
                    <th className="table-header">Lane</th>
                    <th className="table-header">Type</th>
                    <th className="table-header">Size</th>
                    <th className="table-header">Submitted</th>
                    <th className="table-header">Wait</th>
                    <th className="table-header">Runtime</th>
                    <th className="table-header">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {metrics.map((m) => {
                    const waiting = (m.status === 'submitted' || m.status === 'pending');
                    const wait = m.queueSeconds != null
                      ? formatDuration(m.queueSeconds)
                      : waiting
                        ? `${formatDuration((nowTick - new Date(m.submittedAt).getTime()) / 1000)}…`
                        : '—';
                    const run = m.runSeconds != null
                      ? formatDuration(m.runSeconds)
                      : m.status === 'running' && m.startedAt
                        ? `${formatDuration((nowTick - new Date(m.startedAt).getTime()) / 1000)}…`
                        : '—';
                    const toggle = () => setExpandedId(expandedId === m.id ? null : m.id);
                    return (
                      <Fragment key={m.id}>
                        <tr
                          className="table-row cursor-pointer"
                          onClick={toggle}
                          tabIndex={0}
                          role="button"
                          aria-expanded={expandedId === m.id}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
                          }}
                        >
                          <td className="px-6 py-3">
                            <p className="text-sm font-medium text-on-surface truncate max-w-[220px]" title={m.fileName || m.jobName}>
                              {m.fileName || m.jobName}
                            </p>
                            <p className="text-[10px] text-on-surface-variant truncate max-w-[220px]">
                              {m.project?.name || '—'}{m.fileExt ? ` · .${m.fileExt}` : ''}
                            </p>
                          </td>
                          <td className="px-6 py-3 text-xs text-on-surface-variant truncate max-w-[140px]">{userLabel(m.user)}</td>
                          <td className="px-6 py-3"><LaneBadge lane={m.lane} /></td>
                          <td className="px-6 py-3 text-xs text-on-surface-variant">{m.mode}</td>
                          <td className="px-6 py-3 text-xs text-on-surface-variant whitespace-nowrap">{formatSize(m.fileSizeBytes)}</td>
                          <td className="px-6 py-3 text-xs text-on-surface-variant whitespace-nowrap">{formatDate(m.submittedAt)}</td>
                          <td className={`px-6 py-3 text-xs whitespace-nowrap ${waiting ? 'text-amber-600 font-semibold' : 'text-on-surface-variant'}`}>
                            {wait}
                            {(m.crossUserWaitSeconds ?? 0) > 0 && (
                              <p className="text-[10px] text-amber-700">{formatDuration(m.crossUserWaitSeconds)} by others</p>
                            )}
                          </td>
                          <td className="px-6 py-3 text-xs text-on-surface-variant whitespace-nowrap">{run}</td>
                          <td className="px-6 py-3"><StatusBadge status={m.status} /></td>
                        </tr>
                        {expandedId === m.id && (
                          <tr>
                            <td colSpan={9} className="px-6 pb-4 bg-surface-container-low/40">
                              <div className={styles.detailGrid}>
                                <div><span className={styles.detailLabel}>Job name</span><span className={styles.detailMono}>{m.jobName}</span></div>
                                <div><span className={styles.detailLabel}>Batch job ID</span><span className={styles.detailMono}>{m.jobId || '— (submit failed / legacy)'}</span></div>
                                <div><span className={styles.detailLabel}>Batch status</span><span>{m.batchStatus || '—'}{m.exitCode != null ? ` (exit ${m.exitCode})` : ''}</span></div>
                                <div><span className={styles.detailLabel}>Priority</span><span>{m.schedulingPriority ?? '—'}</span></div>
                                <div><span className={styles.detailLabel}>Submitted</span><span>{formatDate(m.submittedAt)}</span></div>
                                <div><span className={styles.detailLabel}>Started</span><span>{formatDate(m.startedAt)}</span></div>
                                <div><span className={styles.detailLabel}>Finished</span><span>{formatDate(m.stoppedAt)}</span></div>
                                <div><span className={styles.detailLabel}>Contended wait (by other users)</span><span>{formatDuration(m.crossUserWaitSeconds)}</span></div>
                                <div><span className={styles.detailLabel}>User</span><span>{m.user?.email || '—'}</span></div>
                              </div>
                              {(m.errorMessage || m.statusReason) && (
                                <div className={styles.detailError}>
                                  {m.errorMessage || m.statusReason}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-outline-variant/10">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex items-center gap-1 text-sm font-medium text-on-surface-variant hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">chevron_left</span>
                    Previous
                  </button>
                  <span className="text-sm text-on-surface-variant">Page {page} of {totalPages} · {total} jobs</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="flex items-center gap-1 text-sm font-medium text-on-surface-variant hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <span className="material-symbols-outlined text-lg">chevron_right</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminConversionStats;
